/**
 * Lightweight programmatic sound effects for Your Life Map.
 *
 * Uses Web Audio API to synthesize short tones on demand (no audio files to load).
 * All plays are gated by a user preference stored in localStorage (key: ylm_sounds_on).
 *
 * Respects browser autoplay policy: the AudioContext is lazily created on first user
 * interaction, so earlier calls are silently queued until unlocked.
 */

const STORAGE_KEY = "ylm_sounds_on";

let audioCtx = null;

const getCtx = () => {
  if (typeof window === "undefined") return null;
  if (audioCtx) return audioCtx;
  const AudioCtxClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtxClass) return null;
  try {
    audioCtx = new AudioCtxClass();
  } catch {
    return null;
  }
  return audioCtx;
};

export const isSoundEnabled = () => {
  if (typeof localStorage === "undefined") return true;
  const v = localStorage.getItem(STORAGE_KEY);
  // Default ON
  return v === null ? true : v === "1";
};

export const setSoundEnabled = (enabled) => {
  try {
    localStorage.setItem(STORAGE_KEY, enabled ? "1" : "0");
  } catch {
    /* ignore */
  }
};

/**
 * Play a single tone.
 * @param {number} frequency Hz
 * @param {number} duration ms
 * @param {number} volume 0..1
 * @param {"sine"|"triangle"|"square"|"sawtooth"} type
 */
const playTone = (frequency, duration = 120, volume = 0.15, type = "sine") => {
  if (!isSoundEnabled()) return;
  const ctx = getCtx();
  if (!ctx) return;

  // Resume if suspended (required by some browsers after autoplay lock)
  if (ctx.state === "suspended") {
    ctx.resume().catch(() => { /* ignore */ });
  }

  try {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(frequency, ctx.currentTime);
    // Attack-decay envelope to avoid click artefacts
    gain.gain.setValueAtTime(0, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(volume, ctx.currentTime + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + duration / 1000);
    osc.connect(gain).connect(ctx.destination);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + duration / 1000 + 0.02);
  } catch {
    /* ignore — user gesture may be required */
  }
};

/**
 * Play a sequence of tones back-to-back.
 * @param {Array<{freq:number, dur:number, vol?:number, type?:string}>} notes
 */
const playSequence = (notes) => {
  if (!isSoundEnabled()) return;
  let delay = 0;
  notes.forEach((n) => {
    setTimeout(() => playTone(n.freq, n.dur, n.vol ?? 0.15, n.type ?? "sine"), delay);
    delay += n.dur;
  });
};

// ===== Preset sounds =====

// Soft "ding" for check-in save (two ascending tones)
export const playSaveChime = () => {
  playSequence([
    { freq: 784, dur: 110, vol: 0.14, type: "sine" },  // G5
    { freq: 1046, dur: 160, vol: 0.14, type: "sine" }, // C6
  ]);
};

// Short "beep" for mic start (rising tone)
export const playMicStart = () => {
  playTone(660, 90, 0.12, "sine");
};

// Short "beep" for mic stop (falling tone)
export const playMicStop = () => {
  playTone(440, 120, 0.12, "sine");
};

// Small error chirp
export const playError = () => {
  playSequence([
    { freq: 330, dur: 120, vol: 0.14, type: "triangle" },
    { freq: 250, dur: 160, vol: 0.14, type: "triangle" },
  ]);
};

// Generic "tap" feedback (unused by default but available)
export const playTap = () => {
  playTone(880, 40, 0.08, "sine");
};
